<script lang="ts">
import { deepmergeCustom } from 'deepmerge-ts';
import {
  Navigation,
  Pagination,
  Scrollbar,
  Keyboard,
  Mousewheel,
  EffectFade,
  Autoplay,
  FreeMode,
} from 'swiper/modules';
import { Swiper } from 'swiper/vue';
import { computed, ref, useSlots } from 'vue';

import { ResponsiveValue, useResponsiveProp } from '@sg-core/resources/assets/js/composables';
import { uid } from '@sg-core/resources/assets/js/utils';

import PhxsgSliderNav from './phxsg-slider-nav.vue';

import type { Swiper as SwiperType } from 'swiper/types';

type SupportedSwiperOptions = Omit<InstanceType<typeof Swiper>['$props'], 'navigation' | 'pagination' | 'scrollbar'>;

export interface PhxsgSliderProps extends /* @vue-ignore */ SupportedSwiperOptions {
  navigation?: ResponsiveValue<boolean>;
  pagination?: ResponsiveValue<boolean>;
  scrollbar?: ResponsiveValue<boolean>;
  prevButtonLabel?: string;
  nextButtonLabel?: string;
  paginationLabel?: string;
  paginationPosition?: 'inner' | 'outer';
  overlay?: boolean;
  navSlot?: 'wrapper-start' | 'container-end';
}

export const mergeOptions = deepmergeCustom<Partial<PhxsgSliderProps>>({
  /**
   * The loose equality operator `!=` will return `false` for both `null` and `undefined`
   * but `true` for everything else including `0`, `''` and `false`.
   */
  filterValues: (values) => values.filter((value) => value != null),
});
</script>

<script setup lang="ts">
const emit = defineEmits<{
  swiper: [swiper: SwiperType];
}>();

defineOptions({
  inheritAttrs: false,
});

const {
  pagination = true,
  navigation = true,
  scrollbar = false,
  prevButtonLabel = 'Previous slide',
  nextButtonLabel = 'Next slide',
  paginationLabel = 'Got to slide',
  paginationPosition = 'outer',
  overlay = false,
  navSlot = 'wrapper-start',
  ...options
} = defineProps<PhxsgSliderProps>();

const slots = useSlots();

const swiperInstance = ref<SwiperType | null>(null);
const translateRef = ref(0);
const transitionRef = ref(0);
const slidesLengthRef = ref(0);
const sliderId = uid('phxsg-slider');
const prevNavId = `${sliderId}-nav-prev`;
const nextNavId = `${sliderId}-nav-next`;
const paginationId = `${sliderId}-pagination`;
const scrollbarId = `${sliderId}-scrollbar`;

const modules = [Navigation, Pagination, Scrollbar, Keyboard, Mousewheel, EffectFade, Autoplay, FreeMode];

const hasAdditionalContent = computed(() => {
  const slot = slots['additional-content']?.();
  return Array.isArray(slot) && slot[0]?.children?.length;
});

const mergedOptions = computed(() => {
  const defaultOptions: PhxsgSliderProps = {
    keyboard: true,
    slidesPerView: 'auto',
    updateOnWindowResize: true,
    modules,
    wrapperClass: 'phxsg-slider__body',
    mousewheel: {
      forceToAxis: true,
    },
  };

  return mergeOptions(defaultOptions, options);
});

const showNavigation = useResponsiveProp(navigation, true);
const showPagination = useResponsiveProp(pagination, true);
const showScrollbar = useResponsiveProp(scrollbar, false);

const isNavInWrapperStart = computed(() => showNavigation.value && navSlot === 'wrapper-start');
const isNavInContainerEnd = computed(() => showNavigation.value && navSlot === 'container-end');

const paginationConfig = computed(() => {
  if (showPagination.value) {
    return {
      clickable: true,
      el: `#${paginationId}`,
      bulletClass: 'phxsg-slider__pagination-bullet',
      bulletActiveClass: 'phxsg-slider__pagination-bullet--active',
      lockClass: 'phxsg-slider__pagination-hidden',
    };
  }

  return false;
});

const scrollbarConfig = computed(() => {
  if (showScrollbar.value) {
    return {
      el: `#${scrollbarId}`,
      draggable: true,
      dragClass: 'phxsg-slider__scrollbar-drag',
      lockClass: 'phxsg-slider__scrollbar-hidden',
    };
  }

  return false;
});

const navigationConfig = computed(() => {
  if (showNavigation.value) {
    return {
      nextEl: `#${nextNavId} .phxsg-btn`,
      prevEl: `#${prevNavId} .phxsg-btn`,
    };
  }

  return false;
});

const translatedStyle = computed(() => ({
  transform: `translateX(${translateRef.value * -1}px)`,
  transitionDuration: `${transitionRef.value}ms`,
}));

const handleSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;
  slidesLengthRef.value = swiper.slides.length;

  swiper.on('setTranslate', ({ params }, translate) => {
    translateRef.value = params.virtualTranslate ? 0 : translate;
  });

  swiper.on('setTransition', (_, transition) => {
    transitionRef.value = transition;
  });

  swiper.on('slidesLengthChange', ({ slides }) => {
    slidesLengthRef.value = slides.length;
  });

  emit('swiper', swiper);
};

defineExpose({
  swiper: swiperInstance,
});
</script>

<template>
  <div class="phxsg-slider-container">
    <Swiper
      :navigation="navigationConfig"
      :pagination="paginationConfig"
      :scrollbar="scrollbarConfig"
      :class="['phxsg-slider']"
      v-bind="{ ...mergedOptions, ...$attrs }"
      @swiper="handleSwiper"
    >
      <template v-if="isNavInWrapperStart" #wrapper-start>
        <PhxsgSliderNav
          :show="slidesLengthRef > 1"
          :style="translatedStyle"
          :prev-label="prevButtonLabel"
          :next-label="nextButtonLabel"
          :prev-id="prevNavId"
          :next-id="nextNavId"
        >
          <template #prev="{ label }">
            <slot name="nav-prev" :label="label" />
          </template>
          <template #next="{ label }">
            <slot name="nav-next" :label="label" />
          </template>
        </PhxsgSliderNav>
      </template>

      <slot />

      <template v-if="overlay" #wrapper-end>
        <div class="phxsg-slider__overlay" :style="translatedStyle" />
      </template>

      <template v-if="showPagination || showScrollbar || isNavInContainerEnd" #container-end>
        <PhxsgSliderNav
          v-if="isNavInContainerEnd"
          :show="slidesLengthRef > 1"
          :prev-label="prevButtonLabel"
          :next-label="nextButtonLabel"
          :prev-id="prevNavId"
          :next-id="nextNavId"
        >
          <template #prev="{ label }">
            <slot name="nav-prev" :label="label" />
          </template>
          <template #next="{ label }">
            <slot name="nav-next" :label="label" />
          </template>
        </PhxsgSliderNav>

        <slot
          v-if="showPagination"
          :id="paginationId"
          name="pagination"
          :class-name="[
            'phxsg-slider__pagination',
            {
              [`phxsg-slider__pagination--${paginationPosition}`]: paginationPosition,
            },
          ]"
          role="tablist"
          :aria-label="paginationLabel"
        />

        <slot v-if="showScrollbar" :id="scrollbarId" name="scrollbar" class-name="phxsg-slider__scrollbar">
          <div :id="scrollbarId" class="phxsg-slider__scrollbar" />
        </slot>

        <div v-if="hasAdditionalContent" class="phxsg-slider__additional-content">
          <slot name="additional-content" />
        </div>
      </template>
    </Swiper>
  </div>
</template>

<style src="./slider.css" />
